import { ConfirmModal } from '@/components/admin/confirm-modal';
import { DataTable } from '@/components/admin/data-table';
import { LetterStatusBadge } from '@/components/admin/status-badge';
import { type AdminLetter, admin } from '@/lib/admin-api';
import { routes } from '@/lib/routes';
import { Flag, Send } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

export default function AdminLettersPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [letters, setLetters] = useState<AdminLetter[]>([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [isLoading, setIsLoading] = useState(true);

  const statusFilter = searchParams.get('status');
  const flaggedFilter = searchParams.get('isFlagged');
  const page = Number(searchParams.get('page')) || 1;

  // Flag modal state
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [selectedLetter, setSelectedLetter] = useState<AdminLetter | null>(null);
  const [flagReason, setFlagReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const fetchLetters = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await admin.letters.list({
        status: statusFilter || undefined,
        isFlagged: flaggedFilter ? flaggedFilter === 'true' : undefined,
        page
      });
      setLetters(result.letters);
      setPagination({
        page: result.pagination.page,
        totalPages: result.pagination.totalPages,
        total: result.pagination.total
      });
    } catch (err) {
      console.error('Failed to fetch letters:', err);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, flaggedFilter, page]);

  useEffect(() => {
    fetchLetters();
  }, [fetchLetters]);

  const handleFilterChange = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    params.delete('page');
    setSearchParams(params);
  };

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(newPage));
    setSearchParams(params);
  };

  const handleFlag = async () => {
    if (!selectedLetter || !flagReason.trim()) return;
    setIsProcessing(true);
    try {
      await admin.letters.flag(selectedLetter.id, { reason: flagReason });
      await fetchLetters();
      setShowFlagModal(false);
      setSelectedLetter(null);
      setFlagReason('');
    } catch (err) {
      console.error('Failed to flag letter:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUnflag = async (letter: AdminLetter) => {
    try {
      await admin.letters.unflag(letter.id);
      await fetchLetters();
    } catch (err) {
      console.error('Failed to unflag letter:', err);
    }
  };

  const handleDeliver = async (letter: AdminLetter) => {
    try {
      await admin.letters.deliver(letter.id);
      await fetchLetters();
    } catch (err) {
      console.error('Failed to deliver letter:', err);
    }
  };

  const columns = [
    {
      key: 'subject',
      header: t('admin.letters.subject'),
      render: (letter: AdminLetter) => (
        <div>
          <p className="font-medium text-ink truncate max-w-xs">{letter.subject}</p>
          <p className="text-xs text-sepia/70">{new Date(letter.createdAt).toLocaleDateString()}</p>
        </div>
      )
    },
    {
      key: 'sender',
      header: t('admin.letters.sender'),
      render: (letter: AdminLetter) => (
        <Link
          to={routes.admin.user(letter.sender.id)}
          className={`text-sm hover:text-blue transition-colors ${
            letter.sender.isAnonymized ? 'text-sepia/50 italic' : 'text-ink'
          }`}
        >
          {letter.sender.username}
        </Link>
      )
    },
    {
      key: 'recipient',
      header: t('admin.letters.recipient'),
      render: (letter: AdminLetter) => (
        <Link
          to={routes.admin.user(letter.recipient.id)}
          className={`text-sm hover:text-blue transition-colors ${
            letter.recipient.isAnonymized ? 'text-sepia/50 italic' : 'text-ink'
          }`}
        >
          {letter.recipient.username}
        </Link>
      )
    },
    {
      key: 'status',
      header: t('admin.letters.status'),
      render: (letter: AdminLetter) => (
        <LetterStatusBadge status={letter.status} isFlagged={letter.isFlagged} />
      )
    },
    {
      key: 'actions',
      header: '',
      render: (letter: AdminLetter) => (
        <div className="flex gap-2 items-center">
          {letter.status === 'IN_TRANSIT' && (
            <button
              type="button"
              onClick={() => handleDeliver(letter)}
              className="flex items-center gap-1 text-xs text-blue hover:text-blue/80 font-medium"
              title={t('admin.letters.deliverNow')}
            >
              <Send className="w-3.5 h-3.5" />
              {t('admin.letters.deliver')}
            </button>
          )}
          {letter.isFlagged ? (
            <button
              type="button"
              onClick={() => handleUnflag(letter)}
              className="text-xs text-sepia/70 hover:text-sepia"
            >
              {t('admin.letters.unflag')}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                setSelectedLetter(letter);
                setShowFlagModal(true);
              }}
              className="text-xs text-red hover:text-red/80"
              title={t('admin.letters.flag')}
            >
              <Flag className="w-4 h-4" />
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-ink">{t('admin.letters.title')}</h1>
        <p className="text-sepia/70 mt-1">{t('admin.letters.subtitle')}</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-warm p-4">
        <div className="flex gap-4">
          <select
            value={statusFilter || ''}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="px-3 py-2 border border-gray-warm rounded-lg focus:ring-2 focus:ring-blue focus:border-blue"
          >
            <option value="">{t('admin.letters.allStatuses')}</option>
            <option value="DRAFT">Draft</option>
            <option value="IN_TRANSIT">In Transit</option>
            <option value="DELIVERED">Delivered</option>
            <option value="READ">Read</option>
          </select>

          <select
            value={flaggedFilter || ''}
            onChange={(e) => handleFilterChange('isFlagged', e.target.value)}
            className="px-3 py-2 border border-gray-warm rounded-lg focus:ring-2 focus:ring-blue focus:border-blue"
          >
            <option value="">{t('admin.letters.allLetters')}</option>
            <option value="true">Flagged only</option>
            <option value="false">Not flagged</option>
          </select>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={letters}
        keyExtractor={(letter) => letter.id}
        pagination={{
          page: pagination.page,
          totalPages: pagination.totalPages,
          total: pagination.total,
          onPageChange: handlePageChange
        }}
        emptyMessage={t('admin.letters.noLetters')}
        isLoading={isLoading}
      />

      {/* Flag Modal */}
      <ConfirmModal
        isOpen={showFlagModal}
        onClose={() => {
          setShowFlagModal(false);
          setSelectedLetter(null);
          setFlagReason('');
        }}
        onConfirm={handleFlag}
        title={t('admin.letters.confirmFlag')}
        description={
          <div className="space-y-4">
            <p>{t('admin.letters.flagDescription')}</p>
            <div>
              <label htmlFor="flagReason" className="block text-sm font-medium text-sepia mb-1">
                {t('admin.letters.flagReason')}
              </label>
              <textarea
                id="flagReason"
                value={flagReason}
                onChange={(e) => setFlagReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-warm rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                rows={3}
                placeholder={t('admin.letters.flagReasonPlaceholder')}
              />
            </div>
          </div>
        }
        confirmText={t('admin.letters.flag')}
        variant="warning"
        isLoading={isProcessing}
      />
    </div>
  );
}
